import type { TrailerWithAxlesResponse } from '@contracts/trailer/TrailerWithAxlesResponse';
import { MenuItem, Select } from '@src/common/components/Select';
import { css } from '@src/common/styles/Styles';
import { useTrailers } from '@src/trailer/hooks/useTrailers';
import { forwardRef, type Ref } from 'react';
import type { ControllerRenderProps, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props extends Partial<ControllerRenderProps> {
  clientId: string;
  error?: FieldError;
  defaultValue?: { label: string; value: string };
  onTrailerSelect: (tractor: TrailerWithAxlesResponse | undefined) => void;
}

export const newTrailerKey = 'new';
export const TrailerSelector = forwardRef(function CustomInput(
  { clientId, onChange, onTrailerSelect, defaultValue, error, ...props }: Props,
  ref: Ref<HTMLDivElement>,
) {
  const { trailers, isLoading } = useTrailers(clientId);

  const { t } = useTranslation();
  const selectOptions = [
    { label: t('newTrailer'), value: newTrailerKey },
    ...(isLoading
      ? defaultValue
        ? [defaultValue]
        : []
      : trailers.trailers.map((trailer) => ({
          value: trailer.id,
          label: trailer.unitNumber,
        }))),
  ];

  return (
    <Select
      ref={ref}
      {...props}
      onChange={(e) => {
        onChange?.(e);
        onTrailerSelect(
          trailers.trailers.find(
            (trailer) => trailer.id === (e.target.value as string),
          ),
        );
      }}
      disabled={isLoading || props.disabled}
      value={props.value || defaultValue?.value || 'new'}
      error={error && error.message}
      className={fullWidth}
      label={t(`trailers`)}
    >
      {selectOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

const fullWidth = css({
  flex: 1,
});
